import { createSlice } from "@reduxjs/toolkit";
import {
  getCollectionDetails,
  getCollectionKPI,
  getCollectionList,
  getDriverPaymentDetailsKPI,
  getDriverPaymentList,
  getDriverSessionPaymentDetails,
  getDriverTripPaymentDetails,
  getFarmerPaymentDetailsKPI,
  getFarmerPaymentList,
  getFarmerTripPaymentDetails,
  getPaymentKPI,
  getTotalPaymentPaidKPI,
  updatePaymentStatus,
  updateTempPaymentStatus,
} from "../APIs/revenueAPI";

const initialState = {
  loading: false,
  error: {},
  status: "idle",

  showToastMessage: {
    visible: false,
    data: {},
  },

  threeDotsPopoverId: 0,

  paymentKPI: {
    total: 0,
    drivers: 0,
    farmers: 0,
  },
  totalPaymentPaid: 0,

  farmerPaymentList: {
    result: [],
    totalRecords: 0,
  },
  driverPaymentList: {
    result: [],
    totalRecords: 0,
  },
  selectedPayment: {},
  paymentDetailsPopoverId: 0,
  selectedPaymentDetailStatusId: 0,

  // driver
  driverTripPaymentDetails: {
    result: [],
    totalDue: 0,
    totalRecords: 0,
  },
  driverSessionPaymentDetails: {
    result: [],
    totalRecords: 0,
  },
  driverPaymentDetailsKPI: {
    paid: 0,
    due: 0,
  },

  // farmer
  farmerTripPaymentDetails: {
    result: [],
    totalDue: 0,
    totalRecords: 0,
  },
  farmerPaymentDetailsKPI: {
    paid: 0,
    due: 0,
  },

  // offtaker
  selectedCollection: {},
  collectionDetailsPopoverId: 0,
  selectedCollectionDetailStatusId: 0,

  collectionKPI: {
    amountDue: 0,
    amountCollected: 0,
  },
  collectionList: {
    result: [],
    totalRecords: 0,
  },
  collectionDetails: {
    result: [],
    totalDue: 0,
    totalRecords: 0,
  },

  transactionHistoryVisible: false,
  editCommodityModal: {
    visible: false,
    current: {},
  },

  collectionListCurrentPage: 1,
  paymentListCurrentPage: 1,
};

export const revenueSlice = createSlice({
  name: "revenue",
  initialState,
  reducers: {
    updateRevenue: (state, action) => {
      state[action.payload.key] = action.payload.value;
    },
  },
  extraReducers(builder) {
    // get payment kpi
    builder.addCase(getPaymentKPI.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getPaymentKPI.fulfilled, (state, action) => {
      state.loading = false;
      state.status = "fulfilled";
      state.paymentKPI = action.payload;
    });
    builder.addCase(getPaymentKPI.rejected, (state, action) => {
      state.loading = false;
      state.status = "rejected";
      state.error = action.error;
    });

    // get total payment paid kpi
    builder.addCase(getTotalPaymentPaidKPI.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getTotalPaymentPaidKPI.fulfilled, (state, action) => {
      state.loading = false;
      state.status = "fulfilled";
      state.totalPaymentPaid = action.payload.total;
    });
    builder.addCase(getTotalPaymentPaidKPI.rejected, (state, action) => {
      state.loading = false;
      state.status = "rejected";
      state.error = action.error;
    });

    // get farmer payment list
    builder.addCase(getFarmerPaymentList.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getFarmerPaymentList.fulfilled, (state, action) => {
      state.loading = false;
      state.status = "fulfilled";
      state.farmerPaymentList = action.payload;
    });
    builder.addCase(getFarmerPaymentList.rejected, (state, action) => {
      state.loading = false;
      state.status = "rejected";
      state.error = action.error;
    });

    // get driver payment list
    builder.addCase(getDriverPaymentList.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getDriverPaymentList.fulfilled, (state, action) => {
      state.loading = false;
      state.status = "fulfilled";
      state.driverPaymentList = action.payload;
    });
    builder.addCase(getDriverPaymentList.rejected, (state, action) => {
      state.loading = false;
      state.status = "rejected";
      state.error = action.error;
    });

    // get farmer trip payment details
    builder.addCase(getFarmerTripPaymentDetails.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getFarmerTripPaymentDetails.fulfilled, (state, action) => {
      state.loading = false;
      state.status = "fulfilled";
      state.farmerTripPaymentDetails = action.payload.response;
    });
    builder.addCase(getFarmerTripPaymentDetails.rejected, (state, action) => {
      state.loading = false;
      state.status = "rejected";
      state.error = action.error;
    });

    // get driver trip payment details
    builder.addCase(getDriverTripPaymentDetails.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getDriverTripPaymentDetails.fulfilled, (state, action) => {
      state.loading = false;
      state.status = "fulfilled";
      state.driverTripPaymentDetails = action.payload.response;
    });
    builder.addCase(getDriverTripPaymentDetails.rejected, (state, action) => {
      state.loading = false;
      state.status = "rejected";
      state.error = action.error;
    });

    // get driver session payment details
    builder.addCase(getDriverSessionPaymentDetails.pending, (state) => {
      state.loading = true;
    });

    builder.addCase(
      getDriverSessionPaymentDetails.fulfilled,
      (state, action) => {
        state.loading = false;
        state.status = "fulfilled";
        state.driverSessionPaymentDetails = action.payload.response;
      },
    );

    builder.addCase(
      getDriverSessionPaymentDetails.rejected,
      (state, action) => {
        state.loading = false;
        state.status = "rejected";
        state.error = action.error;
      },
    );

    // get collection kpi
    builder.addCase(getCollectionKPI.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getCollectionKPI.fulfilled, (state, action) => {
      state.loading = false;
      state.status = "fulfilled";
      state.collectionKPI = action.payload;
    });
    builder.addCase(getCollectionKPI.rejected, (state, action) => {
      state.loading = false;
      state.status = "rejected";
      state.error = action.error;
    });

    // get collection list
    builder.addCase(getCollectionList.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getCollectionList.fulfilled, (state, action) => {
      state.loading = false;
      state.status = "fulfilled";
      state.collectionList = action.payload;
    });
    builder.addCase(getCollectionList.rejected, (state, action) => {
      state.loading = false;
      state.status = "rejected";
      state.error = action.error;
    });

    // get payment details
    builder.addCase(getCollectionDetails.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getCollectionDetails.fulfilled, (state, action) => {
      state.loading = false;
      state.status = "fulfilled";
      state.collectionDetails = action.payload.response;
    });
    builder.addCase(getCollectionDetails.rejected, (state, action) => {
      state.loading = false;
      state.status = "rejected";
      state.error = action.error;
    });

    // update temp payment status
    builder.addCase(updateTempPaymentStatus.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updateTempPaymentStatus.fulfilled, (state) => {
      state.loading = false;
      state.status = "fulfilled";
    });
    builder.addCase(updateTempPaymentStatus.rejected, (state, action) => {
      state.loading = false;
      state.status = "rejected";
      state.error = action.error;
    });

    // update payment status
    builder.addCase(updatePaymentStatus.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updatePaymentStatus.fulfilled, (state) => {
      state.loading = false;
      state.status = "fulfilled";
    });
    builder.addCase(updatePaymentStatus.rejected, (state, action) => {
      state.loading = false;
      state.status = "rejected";
      state.error = action.error;
    });

    // get farmer payment details kpi
    builder.addCase(getFarmerPaymentDetailsKPI.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getFarmerPaymentDetailsKPI.fulfilled, (state, action) => {
      state.loading = false;
      state.status = "fulfilled";
      state.farmerPaymentDetailsKPI = action.payload.result;
    });
    builder.addCase(getFarmerPaymentDetailsKPI.rejected, (state, action) => {
      state.loading = false;
      state.status = "rejected";
      state.error = action.error;
    });

    // get driver payment details kpi
    builder.addCase(getDriverPaymentDetailsKPI.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getDriverPaymentDetailsKPI.fulfilled, (state, action) => {
      state.loading = false;
      state.status = "fulfilled";
      state.driverPaymentDetailsKPI = action.payload.result;
    });
    builder.addCase(getDriverPaymentDetailsKPI.rejected, (state, action) => {
      state.loading = false;
      state.status = "rejected";
      state.error = action.error;
    });
  },
});

export const { updateRevenue } = revenueSlice.actions;

export default revenueSlice.reducer;
